import {
  menuGroupOf,
  NAV_GROUP_ORDER,
  NAV_LABEL_BY_KEY,
  NAV_REACH_BY_PERMISSION,
} from '@/config/navigation';
import type { PermissionItem } from './permission.types';

const ACTION_ORDER = [
  'view',
  'create',
  'edit',
  'delete',
  'export',
  'approve',
  'review',
  'verify',
  'mis_verify',
  'confirm',
  'disburse',
  'amend',
  'assign',
  'download',
  'reset',
  'manage',
  'change_password',
];

const ACTION_LABEL: Record<string, string> = {
  mis_verify: 'MIS verify',
  change_password: 'Password',
};

export function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action.replace(/_/g, ' ');
}

export interface MatrixRow {
  screen: string;
  label: string;
  order: number;
  byAction: Map<string, PermissionItem>;
}

export interface MatrixUnit {
  unitKey: string;
  unit: string;
  actions: string[];
  rows: MatrixRow[];
}

function prefixOf(code: string): string {
  const parts = code.split('.');
  return parts.length > 2 ? parts.slice(0, -1).join('.') : parts[0]!;
}

function actionOf(code: string): string {
  return code.split('.').at(-1)!;
}

const ACRONYM = new Set(['dcr', 'der', 'mis', 'mo', 'url', 'urls', 'rbac']);

// Screens with no menu whose prefix does not read as their name.
const LABEL_BY_PREFIX: Record<string, string> = {
  'user.roles': 'User Access',
};

function labelOf(prefix: string): string {
  const override = LABEL_BY_PREFIX[prefix];
  if (override !== undefined) return override;

  const withoutModule = prefix.includes('.') ? prefix.slice(prefix.indexOf('.') + 1) : prefix;

  return withoutModule
    .split(/[._]/)
    .map((word) =>
      ACRONYM.has(word) ? word.toUpperCase() : word.replace(/^./, (first) => first.toUpperCase()),
    )
    .join(' ');
}

function firstMenuOfModule(
  homeByPrefix: Map<string, { unit: string; key: string; order: number }>,
  module: string,
): { unit: string; key: string; order: number } | undefined {
  let best: { unit: string; key: string; order: number } | undefined;
  for (const [prefix, home] of homeByPrefix) {
    if (!prefix.startsWith(`${module}.`)) continue;
    if (best === undefined || home.order < best.order) best = home;
  }
  return best;
}

export function idsInUnit(
  unit: MatrixUnit,
  action: string | undefined,
  covered: ReadonlyMap<number, unknown>,
): number[] {
  return unit.rows
    .flatMap((row) =>
      action === undefined
        ? [...row.byAction.values()].map((item) => item.id)
        : [row.byAction.get(action)?.id].filter((id): id is number => id !== undefined),
    )
    .filter((id) => !covered.has(id));
}

export type MenuLabelLookup = (key: string) => string | undefined;

const shippedLabel: MenuLabelLookup = (key) => NAV_LABEL_BY_KEY.get(key);

export function buildPermissionMatrix(
  items: PermissionItem[],
  menuLabel: MenuLabelLookup = shippedLabel,
): MatrixUnit[] {
  const units = new Map<string, Map<string, MatrixRow>>();
  const homeByPrefix = new Map<string, { unit: string; key: string; order: number }>();

  function rowAt(unit: string, key: string, screen: string, label: string, order: number) {
    const rows = units.get(unit) ?? new Map<string, MatrixRow>();
    const row = rows.get(key) ?? { screen, label, order, byAction: new Map() };
    row.order = Math.min(row.order, order);
    rows.set(key, row);
    units.set(unit, rows);
    return row;
  }

  // A row is a menu. Every permission that opens one lands on its own row.
  for (const item of items) {
    const reach = NAV_REACH_BY_PERMISSION.get(item.code);
    if (reach === undefined) continue;

    const childKey = reach.opens[0]?.children[0];
    const label =
      (childKey === undefined ? undefined : menuLabel(childKey)) ?? labelOf(prefixOf(item.code));
    const key = `${reach.group}\u203a${childKey ?? label}`;
    rowAt(reach.group, key, prefixOf(item.code), label, reach.order).byAction.set(
      actionOf(item.code),
      item,
    );
    const prefix = prefixOf(item.code);
    const claimed = homeByPrefix.get(prefix);
    if (claimed === undefined || reach.order < claimed.order) {
      homeByPrefix.set(prefix, { unit: reach.group, key, order: reach.order });
    }
  }

  // What opens no menu is an action on one, so it joins the row that shares
  // its code prefix. Only what matches nothing gets a row of its own.
  for (const item of items) {
    if (NAV_REACH_BY_PERMISSION.has(item.code)) continue;

    const prefix = prefixOf(item.code);
    // A module-level code (user.create) is an action on that module's first
    // menu, not a screen of its own.
    const home =
      homeByPrefix.get(prefix) ??
      (prefix.includes('.') ? undefined : firstMenuOfModule(homeByPrefix, prefix));
    const row = home
      ? units.get(home.unit)!.get(home.key)!
      : rowAt(menuGroupOf(item.code), prefix, prefix, labelOf(prefix), Number.MAX_SAFE_INTEGER);
    row.byAction.set(actionOf(item.code), item);
  }

  const unitRank = (unit: string) => {
    const index = NAV_GROUP_ORDER.indexOf(unit);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const actionRank = (action: string) => {
    const index = ACTION_ORDER.indexOf(action);
    return index === -1 ? ACTION_ORDER.length : index;
  };

  return [...units]
    .map(([unit, rows]) => {
      const actions = [
        ...new Set([...rows.values()].flatMap((row) => [...row.byAction.keys()])),
      ].sort((left, right) => actionRank(left) - actionRank(right));
      const ordered = [...rows.values()].sort(
        (left, right) => left.order - right.order || left.label.localeCompare(right.label),
      );
      return { unitKey: unit, unit: menuLabel(unit) ?? unit, actions, rows: ordered };
    })
    .sort((left, right) => unitRank(left.unitKey) - unitRank(right.unitKey));
}
